import { calculatePrice, type FestivalPricingInput } from './calculatePrice.tsx'
import {
	isFreeEvent,
	PAY_WHAT_YOU_CAN_TYPES,
	pricesOf,
	TICKET_TYPE_LABEL,
	type EventPrices as FullEventPrices,
} from './perConcert.ts'

type EventPrices = Partial<FullEventPrices>

export type PriceStep = {
	/** number of concerts bought */
	concerts: number
	/** what one person pays for that many concerts */
	total: number
	/** total / concerts, rounded to whole kroner */
	perConcert: number
}

export type PriceSummary =
	/**
	 * Festival-level pricing (TIERED or SIMPLE): the total per person grows
	 * with the number of concerts, possibly capped (`cap`, SIMPLE pass price).
	 */
	| { kind: 'ladder'; steps: PriceStep[]; cap: number | null }
	/**
	 * 2026: every concert sets its own prices. The ranges cover what paying
	 * costs; a free price (0) stays out, since a free concert says so itself.
	 */
	| {
			kind: 'perConcert'
			adult: { from: number; to: number } | null
			student: { from: number; to: number } | null
			generous: { from: number; to: number } | null
	  }
	/** prices are not published yet */
	| { kind: 'tba' }

/** min–max of the paid prices; unset and free (0) prices are left out */
const paidRange = (values: Array<number | null | undefined>) => {
	const paid = values.filter(
		(value): value is number => value != null && value > 0,
	)
	return paid.length ? { from: Math.min(...paid), to: Math.max(...paid) } : null
}

/**
 * What the front page can honestly say about prices. Never invents a price:
 * unset prices give `tba` instead of the legacy 200 kr fallbacks.
 */
export function getPriceSummary(
	festival: FestivalPricingInput | null,
	events: EventPrices[],
): { summary: PriceSummary; childrenFree: boolean } {
	const model = festival?.pricingModel ?? 'TIERED'

	if (model === 'PER_CONCERT') {
		const adult = paidRange(events.map((event) => event.adultPrice))
		const student = paidRange(events.map((event) => event.studentPrice))
		const generous = paidRange(events.map((event) => event.generousPrice))
		const childrenFree = events.every((event) => !event.childPrice)
		// a child price alone (prefilled from the festival) is not a published
		// price; a concert counts as free only when Ordinær is explicitly 0
		const hasFreeConcert = events.some(
			(event) => event.adultPrice === 0 && isFreeEvent(pricesOf(event)),
		)
		return {
			summary:
				adult || student || generous || hasFreeConcert
					? { kind: 'perConcert', adult, student, generous }
					: { kind: 'tba' },
			childrenFree,
		}
	}

	const childrenFree = !festival?.childPrice
	const concertCount = Math.max(1, Math.min(events.length || 4, 6))

	if (model === 'SIMPLE') {
		const perConcert = festival?.pricePerConcert
		if (perConcert == null) return { summary: { kind: 'tba' }, childrenFree }
		const steps = Array.from({ length: concertCount }, (_, i) => {
			const total = calculatePrice(i + 1, 'adult', festival ?? {})
			return total == null ? [] : [toStep(i + 1, total)]
		}).flat()
		return {
			summary: {
				kind: 'ladder',
				steps,
				cap: festival?.festivalPassPrice ?? null,
			},
			childrenFree,
		}
	}

	// TIERED: 1, 2, 3 and "4 or more" concerts — only the tiers that are set
	const tiers = [
		festival?.priceOne,
		festival?.priceTwo,
		festival?.priceThree,
		festival?.priceFour,
	]
	const steps = tiers.flatMap((total, i) =>
		total == null ? [] : [toStep(i + 1, total)],
	)
	return {
		summary: steps.length
			? { kind: 'ladder', steps, cap: null }
			: { kind: 'tba' },
		childrenFree,
	}
}

const toStep = (concerts: number, total: number): PriceStep => ({
	concerts,
	total,
	perConcert: Math.round(total / concerts),
})

/**
 * From how many concerts the SIMPLE cap (festival pass price) kicks in, i.e.
 * the first count where buying singles would cost more than the cap.
 */
export function capKicksInAt(perConcert: number, cap: number) {
	if (perConcert <= 0) return null
	return Math.floor(cap / perConcert) + 1
}

const formatKr = (amount: number) =>
	amount === 0 ? 'Gratis' : `${amount.toLocaleString('nb-NO')} kr`

const formatKrRange = (range: { from: number; to: number }) =>
	range.from === range.to
		? formatKr(range.from)
		: `${range.from.toLocaleString('nb-NO')}–${range.to.toLocaleString('nb-NO')} kr`

const COUNT_WORDS = ['', 'Éin', 'To', 'Tre', 'Fire', 'Fem', 'Seks']

/**
 * The price list for print (notice, posters) as label/value rows. Built from
 * `getPriceSummary`, so it never states a price the festival has not set:
 * unset prices give no rows at all.
 */
export function getPriceLines(
	festival: FestivalPricingInput | null,
	events: EventPrices[],
): Array<{ label: string; value: string }> {
	const { summary, childrenFree } = getPriceSummary(festival, events)
	const lines: Array<{ label: string; value: string }> = []

	if (summary.kind === 'ladder') {
		const openEnded = festival?.pricingModel !== 'SIMPLE'
		summary.steps.forEach((step, index) => {
			const isLast = index === summary.steps.length - 1
			const word = COUNT_WORDS[step.concerts] ?? String(step.concerts)
			const label =
				step.concerts === 1
					? `${word} konsert`
					: `${word} ${openEnded && isLast ? 'eller fleire ' : ''}konsertar`
			lines.push({ label, value: formatKr(step.total) })
		})
		if (summary.cap != null) {
			lines.push({ label: 'Festivalpass', value: formatKr(summary.cap) })
		}
	} else if (summary.kind === 'perConcert') {
		for (const type of PAY_WHAT_YOU_CAN_TYPES) {
			const prices = summary[type]
			if (!prices) continue
			lines.push({
				label: TICKET_TYPE_LABEL[type],
				value: formatKrRange(prices),
			})
		}
	}

	if (summary.kind !== 'tba' && childrenFree) {
		lines.push({ label: 'Barn fram til 12 år', value: 'Gratis' })
	}
	return lines
}
